/**
 * Каталог товаров v2 — API-клиент.
 *
 * Контракт повторяет каталог макетов: список, фасеты, справочники фильтров,
 * массовые операции и экспорт принимают либо явный список артикулов, либо
 * серверный фильтр (выбор «все N по фильтру» между страницами).
 *
 * Фасеты и справочники — best effort: при недоступности эндпоинта каталог
 * продолжает работать, теряя только счётчики.
 */

import { api } from '../../shared/api/client'
import type { CatalogTag } from '../../components/catalog/tags'
import type {
  ManualProductCreateInput,
  ManualProductCreateResult,
  ManualProductPreflight,
  ManualVisualUploadResult,
  ProductFacets,
  ProductHistoryResponse,
  ProductPresenceResponse,
  ProductVisualPhoto,
  ProductVisualsResponse,
  ProductVisualsSaveResult,
  ShowcaseSectionOption,
} from './types'
import { LEGAL_STATUSES } from '../../shared/legalStatus'

/** Гейтвей отдаёт { success, data } — разворачиваем до data. */
const unwrap = (r: any) => r.data?.data ?? r.data

export interface ProductsV2ListParams {
  limit: number
  offset: number
  search?: string
  /** CSV: category_id */
  category?: string
  /** CSV: brand_ru макета-донора */
  brand?: string
  /** CSV: type макета-донора */
  designType?: string
  /** CSV: category макета-донора */
  designCategory?: string
  /** CSV: theme макета-донора */
  theme?: string
  /** CSV: значений persona макета-донора */
  persona?: string
  /** wb | ozon | both | none */
  marketplace?: string
  gender?: string
  age?: string
  occasion?: string
  seasonal?: string
  legal_status?: string
  design_state?: string
  one_c_status?: string
  hasPreview?: 'true' | 'false'
  hasBarcode?: 'true' | 'false'
  priceFrom?: string
  priceTo?: string
  sellerId?: string
  /** design | external */
  origin?: string
  dateFrom?: string
  dateTo?: string
  tags?: string
  noTags?: string
  /** CSV значений для отбора по списку: артикул, штрихкод, nmID ВБ, артикул поставщика, код макета */
  articles?: string
  /**
   * Режим проверки списка: '1' | 'true'. Прочие фильтры (кроме articles)
   * игнорируются, тяжёлый запрос не выполняется — в ответе только `articles_matched`.
   */
  articles_resolve?: string
  /** Товары в архиве (deleted_at IS NOT NULL) */
  archived?: 'true'
  /** Требуют внимания */
  attention?: 'true'
  /** Есть карточка МП в статусе selling */
  selling?: 'true'
  /** Есть карточка МП в статусе draft — прогнан конвейером, но не выгружен */
  draft?: 'true'
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export type ProductsV2FilterParams = Omit<
  ProductsV2ListParams,
  'limit' | 'offset' | 'sortBy' | 'sortOrder'
>

/** Цель массовой операции: явный список либо фильтр */
export type ProductSelectionPayload =
  | { articles: string[] }
  | { filters: ProductsV2FilterParams }

export interface ProductFilterOptions {
  categories: Array<{ id: number; description: string; prefix?: string; price?: number | null; site_price?: number | null }>
  brands: string[]
  designTypes: string[]
  designCategories: string[]
  themes: string[]
  personas: string[]
  genders: string[]
  ages: string[]
  occasions: string[]
  legalStatuses: string[]
  designStates: string[]
  sellers: Array<{ id: number; name: string; marketplace?: string; cabinet_code?: string | null }>
}

export type ProductExportFormat = 'csv' | 'xlsx'

const EMPTY_FILTER_OPTIONS: ProductFilterOptions = {
  categories: [],
  brands: [],
  designTypes: [],
  designCategories: [],
  themes: [],
  personas: [],
  genders: [],
  ages: [],
  occasions: [],
  legalStatuses: [],
  designStates: [],
  sellers: [],
}

export const productsV2API = {
  /**
   * Список товаров. Возвращает { products, total }.
   * Архив запрашивается тем же эндпоинтом с archived=true.
   */
  async getProducts(
    params: ProductsV2ListParams,
    signal?: AbortSignal,
  ): Promise<{ products: any[]; total: number; articles_matched?: string[] }> {
    const response = await api.get('/products/catalog', { params, signal })
    const data = unwrap(response) || {}
    const products = Array.isArray(data) ? data : data.products || []
    const total = Number(data.pagination?.total ?? data.total)
    return {
      products,
      total: Number.isFinite(total) ? total : products.length,
      articles_matched: Array.isArray(data.articles_matched) ? data.articles_matched : undefined,
    }
  },

  /**
   * Проверка списка артикулов на сервере: тот же запрос каталога с
   * articles_resolve=1 — сервер игнорирует прочие фильтры и отвечает формой
   * { products: [], pagination: { limit: 0, offset: 0, total: 0 }, articles_matched }.
   * Возвращает подмножество переданных значений, для которых нашлась карточка.
   */
  async resolveArticles(values: string[]): Promise<string[]> {
    if (values.length === 0) return []
    const r = await productsV2API.getProducts({
      limit: 1,
      offset: 0,
      articles: values.join(','),
      articles_resolve: '1',
    })
    return r.articles_matched ?? []
  },

  /** Фасетные счётчики для текущего набора серверных фильтров. */
  async getFacets(params: ProductsV2ListParams, signal?: AbortSignal): Promise<ProductFacets> {
    const response = await api.get('/products/facets', { params, signal })
    return unwrap(response) || {}
  },

  /** Справочники значений для фильтров */
  async getFilterOptions(): Promise<ProductFilterOptions> {
    const response = await api.get('/products/filter-options')
    const data = unwrap(response) || {}
    const strings = (key: keyof ProductFilterOptions): string[] =>
      Array.isArray(data[key]) ? data[key].map(String).filter(Boolean) : []
    return {
      categories: Array.isArray(data.categories) ? data.categories : [],
      brands: strings('brands'),
      designTypes: strings('designTypes'),
      designCategories: strings('designCategories'),
      themes: strings('themes'),
      personas: strings('personas'),
      genders: strings('genders'),
      ages: strings('ages'),
      occasions: strings('occasions'),
      legalStatuses: [...LEGAL_STATUSES],
      designStates: strings('designStates'),
      sellers: Array.isArray(data.sellers) ? data.sellers : [],
    }
  },

  async getProduct(article: string) {
    const encoded = encodeURIComponent(article)
    const [detailResponse, catalogResponse] = await Promise.all([
      api.get(`/products/${encoded}`),
      api.get('/products/catalog', { params: { articles: article, limit: 1, offset: 0 } }),
    ])
    const detail = unwrap(detailResponse) || {}
    const catalogData = unwrap(catalogResponse) || {}
    const catalogRows = Array.isArray(catalogData)
      ? catalogData
      : Array.isArray(catalogData.products)
        ? catalogData.products
        : []
    return { ...detail, ...(catalogRows[0] || {}) }
  },

  async getShowcaseSections(): Promise<ShowcaseSectionOption[]> {
    const response = await api.get('/seo-dictionaries', { params: { field: 'category' } })
    const data = unwrap(response)
    const rows = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []
    return rows
      .map((row: any) => ({
        id: Number(row.id),
        value: String(row.value ?? ''),
        siteSection: row.props?.site_section ? String(row.props.site_section) : null,
      }))
      .filter((row: ShowcaseSectionOption) => Number.isFinite(row.id) && Boolean(row.value))
  },

  async getManualProductCategories(): Promise<ProductFilterOptions['categories']> {
    const response = await api.get('/categories')
    const data = unwrap(response)
    const rows = Array.isArray(data) ? data : Array.isArray(data?.categories) ? data.categories : []
    return rows
      .map((row: any) => ({
        id: Number(row.category_id ?? row.id),
        description: String(row.description ?? row.name ?? ''),
        prefix: row.prefix ? String(row.prefix) : undefined,
        price: row.price == null ? null : Number(row.price),
        site_price: row.site_price == null ? null : Number(row.site_price),
      }))
      .filter((row: ProductFilterOptions['categories'][number]) => Number.isFinite(row.id) && Boolean(row.description))
  },

  preflightManualProduct(input: {
    category_id: number
    title?: string
    showcase_category?: string
  }): Promise<ManualProductPreflight> {
    return api.post('/products/manual/preflight', input).then(unwrap)
  },

  createManualProduct(input: ManualProductCreateInput): Promise<ManualProductCreateResult> {
    return api.post('/products/manual', input, { timeout: 30_000 }).then(unwrap)
  },

  uploadManualVisual(input: {
    article: string
    cabinet: string
    image: string
    format: 'jpg' | 'jpeg' | 'png' | 'webp'
  }): Promise<ManualVisualUploadResult> {
    return api.post('/products/visuals/upload', input).then(unwrap)
  },

  /** Где артикул присутствует на площадках: зеркала витрин + карточки ПИМ */
  getPresence(article: string): Promise<ProductPresenceResponse> {
    return api.get(`/products/${encodeURIComponent(article)}/presence`).then(unwrap)
  },

  /** Лента событий по артикулу, новые сверху */
  getHistory(article: string, limit = 100): Promise<ProductHistoryResponse> {
    return api.get(`/products/${encodeURIComponent(article)}/history`, { params: { limit } }).then(unwrap)
  },

  getProductVisuals(article: string): Promise<ProductVisualsResponse> {
    return api.get(`/products/${encodeURIComponent(article)}/visuals`).then(unwrap)
  },

  /**
   * Записать набор визуалов кабинета.
   *
   * У товара, созданного конвейером (с макетом), сервер принимает набор только
   * при явном `allowPipelineProduct`: ручной порядок заменит следующая
   * перегенерация воронки, и клиент обязан это знать. Без флага прежний отказ.
   * `journaled` — запись журнала правок создана (карточку кабинета можно
   * переотправить), `affected_cards` — сколько карточек она затронет.
   */
  setProductVisuals(
    article: string,
    cabinet: string,
    photos: ProductVisualPhoto[],
    options?: { allowPipelineProduct?: boolean },
  ): Promise<ProductVisualsSaveResult> {
    return api
      .put(`/products/${encodeURIComponent(article)}/visuals`, {
        cabinet,
        photos: photos.map(({ url, sha256 }) => ({ url, sha256: sha256 || null })),
        ...(options?.allowPipelineProduct ? { allow_pipeline_product: true } : {}),
      })
      .then(unwrap)
  },

  rebuildProductSiteCard(article: string) {
    return api.post(`/products/${encodeURIComponent(article)}/site-card`).then(unwrap)
  },
  updateProduct: (article: string, updates: any) =>
    api.put(`/products/${encodeURIComponent(article)}`, updates).then(unwrap),
  deleteProduct: (article: string) =>
    api.delete(`/products/${encodeURIComponent(article)}`).then(unwrap),

  // ── Теги ──
  /** Словарь тегов общий с каталогом макетов; счётчики — по товарам */
  async getTags(): Promise<CatalogTag[]> {
    const response = await api.get('/products/tags')
    const data = unwrap(response) || {}
    return Array.isArray(data) ? data : data.tags || []
  },
  createTag: (value: string, color: string): Promise<CatalogTag> =>
    api.post('/products/tags', { value, color }).then(unwrap),
  setProductTags: (article: string, tagIds: number[]) =>
    api.put(`/products/${encodeURIComponent(article)}/tags`, { tag_ids: tagIds }).then(unwrap),
  bulkTags: (selection: ProductSelectionPayload, add: number[], remove: number[]) =>
    api.post('/products/bulk-tags', { ...selection, add, remove }).then(unwrap),

  massUpdate: (selection: ProductSelectionPayload, updates: any) =>
    api.post('/products/mass-update', { ...selection, updates }).then(unwrap),
  massDelete: (selection: ProductSelectionPayload) =>
    api.post('/products/mass-delete', selection).then(unwrap),
  massRestore: (selection: ProductSelectionPayload) =>
    api.post('/products/mass-restore', selection).then(unwrap),
  massPermanentDelete: (selection: ProductSelectionPayload) =>
    api.post('/products/mass-permanent-delete', selection).then(unwrap),

  /** Экспорт CSV/XLSX: без selection — весь доступный scope. */
  exportProducts: (format: ProductExportFormat, selection?: ProductSelectionPayload) =>
    api.get('/products/export', {
      params: selection
        ? 'filters' in selection
          ? { format, filters: JSON.stringify(selection.filters) }
          : { format, articles: selection.articles.join(',') }
        : { format },
      responseType: 'blob',
    }),
}

export { EMPTY_FILTER_OPTIONS }
